/* ------------------------------------------------------------------ *
 * chr-fitment.js — Canada Hitch Rack vehicle selector ("garage")
 *
 * Cascading Make -> Model -> Year, backed by window.CHR_FITMENT (built
 * from product fitment metafields by build-fitment-asset.mjs). All data
 * is inline, so the dropdowns populate with no network request.
 *
 * The chosen vehicle is kept in localStorage under CHR_GARAGE_KEY and
 * broadcast as a `chr:garage` event, so the header chip and the product
 * page fitment banner can react without re-reading storage themselves.
 * ------------------------------------------------------------------ */

const CHR_GARAGE_KEY = 'chr_garage_v1';

/* ------------------------------- store ------------------------------- */

const ChrGarage = {
  get() {
    try {
      const raw = localStorage.getItem(CHR_GARAGE_KEY);
      if (!raw) return null;
      const v = JSON.parse(raw);
      return v && v.make ? v : null;
    } catch (e) {
      // Private mode, blocked storage, or corrupt value — behave as empty.
      return null;
    }
  },

  set(vehicle) {
    try {
      localStorage.setItem(CHR_GARAGE_KEY, JSON.stringify(vehicle));
    } catch (e) {
      /* non-fatal: the selection still applies to this navigation */
    }
    document.dispatchEvent(new CustomEvent('chr:garage', { detail: vehicle }));
  },

  clear() {
    try {
      localStorage.removeItem(CHR_GARAGE_KEY);
    } catch (e) {
      /* ignore */
    }
    document.dispatchEvent(new CustomEvent('chr:garage', { detail: null }));
  },

  label(v) {
    if (!v) return '';
    const model = v.model && v.model !== window.CHR_FITMENT?.allModelsLabel ? ' ' + v.model : '';
    return [v.year, v.make + model].filter(Boolean).join(' ');
  }
};

window.ChrGarage = ChrGarage;

/* ------------------------------ helpers ------------------------------ */

function fitmentData() {
  return window.CHR_FITMENT || { makes: {}, hidden: [], aliases: {}, allModelsLabel: '' };
}

function visibleMakes() {
  const d = fitmentData();
  const hidden = new Set(d.hidden || []);
  return Object.keys(d.makes).filter((m) => !hidden.has(m)).sort();
}

// A make plus any distributor labels that mean the same platform.
// Chevrolet and GMC both fold in "GM (Chevrolet/GMC)".
function makeGroup(make) {
  const d = fitmentData();
  return [make].concat((d.aliases && d.aliases[make]) || []);
}

function modelsFor(make) {
  const d = fitmentData();
  const out = {};
  for (const key of makeGroup(make)) {
    Object.assign(out, d.makes[key] || {});
  }
  return out;
}

function yearsFor(make, model) {
  const models = modelsFor(make);
  const range = models[model];
  if (!range) {
    // Model has fitment but no year data — offer a sensible modern span.
    const now = new Date().getFullYear() + 1;
    return Array.from({ length: now - 1985 + 1 }, (_, i) => now - i);
  }
  const [lo, hi] = range;
  const out = [];
  for (let y = hi; y >= lo; y--) out.push(y);
  return out;
}

function fillSelect(select, values, placeholder) {
  select.innerHTML = '';
  const first = document.createElement('option');
  first.value = '';
  first.textContent = placeholder;
  select.appendChild(first);
  for (const v of values) {
    const o = document.createElement('option');
    o.value = v;
    o.textContent = v;
    select.appendChild(o);
  }
}

// Where "show me what fits" goes. Shopify storefront filters accept the
// same parameter repeated for an OR match, which is how the GM alias is
// folded in alongside Chevrolet or GMC.
function resultsUrl(vehicle, basePath) {
  const params = new URLSearchParams();
  for (const mk of makeGroup(vehicle.make)) {
    params.append('filter.p.m.fitment.makes', mk);
  }
  if (vehicle.model && vehicle.model !== fitmentData().allModelsLabel) {
    params.append('filter.p.m.fitment.models', vehicle.model);
  }
  if (vehicle.year) params.append('chr_year', vehicle.year);
  return (basePath || '/collections/all') + '?' + params.toString();
}

window.ChrFitmentUrl = resultsUrl;

/* ---------------------------- the element ---------------------------- */

class ChrFitmentSelector extends HTMLElement {
  connectedCallback() {
    this.makeEl = this.querySelector('[data-chr-make]');
    this.modelEl = this.querySelector('[data-chr-model]');
    this.yearEl = this.querySelector('[data-chr-year]');
    this.submitEl = this.querySelector('[data-chr-submit]');
    this.formEl = this.querySelector('form');
    this.basePath = this.dataset.resultsPath || '/collections/all';

    if (!this.makeEl || !this.modelEl || !this.yearEl) return;

    fillSelect(this.makeEl, visibleMakes(), 'Make');
    this.reset(this.modelEl, 'Model');
    this.reset(this.yearEl, 'Year');

    this.makeEl.addEventListener('change', () => this.onMake());
    this.modelEl.addEventListener('change', () => this.onModel());
    this.yearEl.addEventListener('change', () => this.refreshSubmit());

    if (this.formEl) {
      this.formEl.addEventListener('submit', (e) => {
        e.preventDefault();
        this.go();
      });
    }

    // Re-select whatever is already in the garage.
    const saved = ChrGarage.get();
    if (saved && this.dataset.prefill !== 'false') this.prefill(saved);

    this.refreshSubmit();
  }

  reset(select, placeholder) {
    fillSelect(select, [], placeholder);
    select.disabled = true;
  }

  onMake() {
    const make = this.makeEl.value;
    if (!make) {
      this.reset(this.modelEl, 'Model');
      this.reset(this.yearEl, 'Year');
      return this.refreshSubmit();
    }
    const models = Object.keys(modelsFor(make));
    const label = fitmentData().allModelsLabel;
    fillSelect(
      this.modelEl,
      models.map((m) => (m === label ? 'Other / not listed' : m)),
      'Model'
    );
    this.modelEl.disabled = false;
    this.reset(this.yearEl, 'Year');
    this.refreshSubmit();
  }

  onModel() {
    const make = this.makeEl.value;
    let model = this.modelEl.value;
    if (model === 'Other / not listed') model = fitmentData().allModelsLabel;
    if (!make || !model) {
      this.reset(this.yearEl, 'Year');
      return this.refreshSubmit();
    }
    fillSelect(this.yearEl, yearsFor(make, model), 'Year');
    this.yearEl.disabled = false;
    this.refreshSubmit();
  }

  refreshSubmit() {
    if (!this.submitEl) return;
    // Make alone is enough to search; model and year narrow it.
    this.submitEl.disabled = !this.makeEl.value;
  }

  prefill(v) {
    if (!Object.prototype.hasOwnProperty.call(fitmentData().makes, v.make) &&
        visibleMakes().indexOf(v.make) === -1) return;
    this.makeEl.value = v.make;
    this.onMake();
    if (v.model) {
      const label = fitmentData().allModelsLabel;
      this.modelEl.value = v.model === label ? 'Other / not listed' : v.model;
      this.onModel();
      if (v.year) this.yearEl.value = String(v.year);
    }
    this.refreshSubmit();
  }

  go() {
    const make = this.makeEl.value;
    if (!make) return;
    let model = this.modelEl.value;
    if (model === 'Other / not listed') model = fitmentData().allModelsLabel;
    const vehicle = { make, model: model || '', year: this.yearEl.value || '' };
    ChrGarage.set(vehicle);
    window.location.href = resultsUrl(vehicle, this.basePath);
  }
}

if (!customElements.get('chr-fitment-selector')) {
  customElements.define('chr-fitment-selector', ChrFitmentSelector);
}

/* ----------------------- product-page fit check ----------------------- */

// Does the saved vehicle fall inside one of this product's fitment rows?
// A row with no model matches any model of that make; a row with no years
// matches any year. Chevrolet/GMC also satisfy a "GM (Chevrolet/GMC)" row.
function matchesVehicle(rows, vehicle) {
  if (!vehicle || !Array.isArray(rows)) return null;
  const group = makeGroup(vehicle.make).map((s) => s.toLowerCase());
  const year = vehicle.year ? parseInt(vehicle.year, 10) : null;
  const wantModel = (vehicle.model || '').toLowerCase();
  const allLabel = (fitmentData().allModelsLabel || '').toLowerCase();

  for (const row of rows) {
    if (!row || !row.make) continue;
    if (group.indexOf(String(row.make).toLowerCase()) === -1) continue;

    const rowModel = (row.model || '').toLowerCase();
    if (rowModel && wantModel && wantModel !== allLabel && rowModel !== wantModel) continue;

    if (year && row.yearFrom && row.yearTo) {
      if (year < row.yearFrom || year > row.yearTo) continue;
    }
    return row;
  }
  return false;
}

window.ChrMatchesVehicle = matchesVehicle;

class ChrFitmentCheck extends HTMLElement {
  connectedCallback() {
    this.verdictEl = this.querySelector('[data-chr-verdict]');
    try {
      this.rows = JSON.parse(this.dataset.fitment || '[]');
    } catch (e) {
      this.rows = [];
    }
    this.render();
    this._onGarage = () => this.render();
    document.addEventListener('chr:garage', this._onGarage);
  }

  disconnectedCallback() {
    if (this._onGarage) document.removeEventListener('chr:garage', this._onGarage);
  }

  render() {
    if (!this.verdictEl) return;
    const v = ChrGarage.get();
    if (!v) {
      this.verdictEl.hidden = true;
      this.classList.remove('chr-fitcheck--yes', 'chr-fitcheck--no');
      return;
    }
    const hit = matchesVehicle(this.rows, v);
    const label = ChrGarage.label(v);
    this.verdictEl.hidden = false;
    this.classList.toggle('chr-fitcheck--yes', !!hit);
    this.classList.toggle('chr-fitcheck--no', !hit);
    this.verdictEl.textContent = hit
      ? 'Fits your ' + label
      : 'Does not fit your ' + label;
  }
}

if (!customElements.get('chr-fitment-check')) {
  customElements.define('chr-fitment-check', ChrFitmentCheck);
}

/* --------------------------- collection gate --------------------------- */

const MAKE_PARAM = 'filter.p.m.fitment.makes';
const MODEL_PARAM = 'filter.p.m.fitment.models';

// Is the current collection already narrowed to the saved vehicle?
function filterApplied(vehicle) {
  const p = new URLSearchParams(window.location.search);
  const makes = p.getAll(MAKE_PARAM).map((s) => s.toLowerCase());
  if (!makes.length) return false;
  return makeGroup(vehicle.make).some((m) => makes.indexOf(m.toLowerCase()) !== -1);
}

// Apply the vehicle filter to whatever collection we are already on,
// preserving sort and any other filters the shopper has set.
function narrowedUrl(vehicle) {
  const p = new URLSearchParams(window.location.search);
  p.delete(MAKE_PARAM);
  p.delete(MODEL_PARAM);
  for (const mk of makeGroup(vehicle.make)) p.append(MAKE_PARAM, mk);
  if (vehicle.model && vehicle.model !== fitmentData().allModelsLabel) {
    p.append(MODEL_PARAM, vehicle.model);
  }
  return window.location.pathname + '?' + p.toString();
}

function widenedUrl() {
  const p = new URLSearchParams(window.location.search);
  p.delete(MAKE_PARAM);
  p.delete(MODEL_PARAM);
  const q = p.toString();
  return window.location.pathname + (q ? '?' + q : '');
}

class ChrFitmentGate extends HTMLElement {
  connectedCallback() {
    this.titleEl = this.querySelector('[data-chr-gate-title]');
    this.subEl = this.querySelector('[data-chr-gate-sub]');
    this.actionsEl = this.querySelector('[data-chr-gate-actions]');
    this.gateEl = this.querySelector('[data-chr-gate]');
    this.selectPath = this.dataset.selectPath || '/';
    this.render();
    this._onGarage = () => this.render();
    document.addEventListener('chr:garage', this._onGarage);
  }

  disconnectedCallback() {
    if (this._onGarage) document.removeEventListener('chr:garage', this._onGarage);
  }

  button(label, href, secondary) {
    const a = document.createElement('a');
    a.className = 'button button--small' + (secondary ? ' button--secondary' : '');
    a.href = href;
    a.textContent = label;
    return a;
  }

  render() {
    if (!this.titleEl || !this.actionsEl) return;
    const v = ChrGarage.get();
    this.actionsEl.innerHTML = '';
    this.hidden = false;

    if (!v) {
      this.gateEl.classList.remove('chr-gate--active');
      this.titleEl.textContent = 'Shopping for a specific vehicle?';
      this.subEl.textContent =
        'Pick your vehicle once and we will show only the parts confirmed to fit it.';
      this.actionsEl.appendChild(this.button('Select your vehicle', this.selectPath));
      return;
    }

    const label = ChrGarage.label(v);

    if (filterApplied(v)) {
      this.gateEl.classList.add('chr-gate--active');
      this.titleEl.textContent = 'Showing parts that fit your ' + label;
      this.subEl.textContent =
        'Parts that do not list this vehicle are hidden. Fitment comes from the manufacturer part data.';
      this.actionsEl.appendChild(this.button('Show everything', widenedUrl(), true));
      this.actionsEl.appendChild(this.button('Change vehicle', this.selectPath, true));
      return;
    }

    this.gateEl.classList.remove('chr-gate--active');
    this.titleEl.textContent = 'You have a ' + label + ' saved';
    this.subEl.textContent = 'Narrow this page to parts confirmed to fit it.';
    this.actionsEl.appendChild(this.button('Show what fits', narrowedUrl(v)));
    this.actionsEl.appendChild(this.button('Change vehicle', this.selectPath, true));
  }
}

if (!customElements.get('chr-fitment-gate')) {
  customElements.define('chr-fitment-gate', ChrFitmentGate);
}

/* --------------------------- garage display --------------------------- */

// Any element with [data-chr-garage-label] shows the current vehicle;
// [data-chr-garage-empty] shows when nothing is selected.
function paintGarage() {
  const v = ChrGarage.get();
  document.querySelectorAll('[data-chr-garage-label]').forEach((el) => {
    el.textContent = v ? ChrGarage.label(v) : '';
  });
  document.querySelectorAll('[data-chr-garage-set]').forEach((el) => {
    el.hidden = !v;
  });
  document.querySelectorAll('[data-chr-garage-empty]').forEach((el) => {
    el.hidden = !!v;
  });
}

// More than one section may include this file (the hero and the vehicle
// picks band, say). The custom-element defines above are already guarded;
// these document-level listeners need the same treatment or a second copy
// would double-bind the "clear" buttons.
if (!window.__chrGarageBound) {
  window.__chrGarageBound = true;

  document.addEventListener('chr:garage', paintGarage);
  document.addEventListener('DOMContentLoaded', () => {
    paintGarage();
    document.querySelectorAll('[data-chr-garage-clear]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        ChrGarage.clear();
      });
    });
  });
}
